import { DateTime } from "luxon";
const DATE_FORMAT = "yyyy-MM-dd";
const ISO_FORMAT = "yyyy-LL-dd'T'HH:mm:ss.SSSZZZ";
/**
 * Formats a date to yyyy-MM-dd format
 * @param {Date} date - Date to format
 * @param {string} del - Delimiter to use between year, month, and day
 * @returns {string} Formatted date string
 */
export function formatDateToyyyyMMdd(date, del) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}${del}${month}${del}${day}`;
}

// Given an isoStr in the format YYYY-MM-DDTHH:mm:ss.SSSZZZ, return a string in the format YYYY-MM-DD
export function getDateFromIsoStr(isoStr) {
  // Confirm isoStr is in ISO_FORMAT
  const parsedDate = DateTime.fromFormat(isoStr, ISO_FORMAT);
  if (!parsedDate.isValid) {
    throw new Error(
      `Invalid date format: ${isoStr}. Expected format is ${ISO_FORMAT}`
    );
  }

  // Luxon's docs: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
  const datePart = DateTime.fromISO(isoStr).toFormat(DATE_FORMAT);
  return datePart;
}

// Given a date in the format YYYY-MM-DD, attach the current time to it and return an ISO string in the format YYYY-MM-DDTHH:mm:ss.SSSZZZ
export function attachCurrTimeToDate(newDate) {
  // Confirm new date is in format YYYY-MM-DD
  const parsedDate = DateTime.fromFormat(newDate, DATE_FORMAT);
  if (!parsedDate.isValid) {
    throw new Error(
      `Invalid date format: ${newDate}. Expected format is ${DATE_FORMAT}`
    );
  }

  // Get current time in UTC directly
  // Luxon's docs: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
  // NOTE: Need to explictly indicate "utc", since Luxon converts the date to the local timezone when parsing
  const timePart = DateTime.utc().toFormat("HH:mm:ss.SSSZZZ");
  return `${newDate}T${timePart}`;
}

export function attachCurrTzStartTimeAsUtcToDate(date) {
  // Confirm new date is in format YYYY-MM-DD
  const parsedDate = DateTime.fromFormat(date, DATE_FORMAT);
  if (!parsedDate.isValid) {
    throw new Error(
      `Invalid date format: ${date}. Expected format is ${DATE_FORMAT}`
    );
  }

  // Parse the date components and explicitly create a DateTime in the local timezone
  const [year, month, day] = date.split("-").map(Number);
  const startOfDayInLocalTz = DateTime.local(year, month, day).startOf("day");

  // Convert to UTC (this will shift the time based on timezone offset)
  const startOfDayInUtc = startOfDayInLocalTz.toUTC();
  return startOfDayInUtc.toFormat(ISO_FORMAT);
}

export function attachCurrTzEndTimeAsUtcToDate(date) {
  // Confirm new date is in format YYYY-MM-DD
  const parsedDate = DateTime.fromFormat(date, DATE_FORMAT);
  if (!parsedDate.isValid) {
    throw new Error(
      `Invalid date format: ${date}. Expected format is ${DATE_FORMAT}`
    );
  }

  // Parse the date components and explicitly create a DateTime in the local timezone
  const [year, month, day] = date.split("-").map(Number);
  const endOfDayInLocalTz = DateTime.local(year, month, day).endOf("day");

  // Convert to UTC (this will shift the time based on timezone offset)
  const endOfDayInUtc = endOfDayInLocalTz.toUTC();
  return endOfDayInUtc.toFormat(ISO_FORMAT);
}

export async function getThisWeeksMonday() {
  // Calculate this week's monday in format YYYY-MM-DD
  const monday = new Date();
  const dayIdx = monday.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  monday.setDate(monday.getDate() - (dayIdx === 0 ? 6 : dayIdx - 1)); // Adjust to get to Monday (if Sunday, go back 6 days)
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, "0");
  const date = String(monday.getDate()).padStart(2, "0");
  return `${year}-${month}-${date}`;
}
